import { Shield, CheckCircle, AlertTriangle, FileText } from 'lucide-react';

const CHECKS = [
  { label: 'TLS 1.2+ enforced on all SIP trunks',    status: 'pass' },
  { label: 'SRTP enabled on all media paths',          status: 'pass' },
  { label: 'SBC management interface on private IP only', status: 'pass' },
  { label: 'IP whitelist applied to SIP signalling',   status: 'pass' },
  { label: 'Voxbone API key last rotated',       status: 'warn', detail: '112 days ago — recommend rotating every 90 days' },
  { label: 'AudioCodes firmware up to date on all SBCs', status: 'warn', detail: 'SBC-CENTRAL-01 is 2 versions behind' },
  { label: 'E911 emergency routing configured',        status: 'pass' },
  { label: 'STIR/SHAKEN attestation (via Bandwidth)',  status: 'pass' },
  { label: 'Call recording consent notices active',    status: 'pass' },
  { label: 'Audit logging enabled',                    status: 'pass' },
];

const AUDIT_LOG = [
  { user: 'admin@damovo-customer.com', action: 'Number +12125550201 CNAM changed to "Damovo Customer NY"', ts: '2026-05-19 14:32' },
  { user: 'admin@damovo-customer.com', action: 'Routing rule "NY Contact Centre" priority changed from 1 to 2', ts: '2026-05-19 11:15' },
  { user: 'system',             action: 'SBC-WEST-01 firmware updated to 7.40A.258', ts: '2026-05-18 03:12' },
  { user: 'admin@damovo-customer.com', action: 'Platform NICE CXone endpoint updated', ts: '2026-05-17 16:44' },
  { user: 'system',             action: 'Port-in order PO-2024-0879 completed (+14085550398)', ts: '2026-05-17 09:01' },
  { user: 'admin@damovo-customer.com', action: 'Voxbone trunk BW-SOUTH-01 capacity change request submitted', ts: '2026-05-16 15:28' },
  { user: 'admin@damovo-customer.com', action: 'API key rotated — Voxbone token', ts: '2026-01-28 10:05' },
];

export default function Security() {
  const passCount = CHECKS.filter(c => c.status === 'pass').length;
  const warnCount = CHECKS.filter(c => c.status === 'warn').length;
  const score = Math.round((passCount / CHECKS.length) * 100);

  return (
    <div className="content">
      <div className="grid-2 mb-20">
        <div className="card">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: `conic-gradient(${score > 80 ? 'var(--ok)' : 'var(--warn)'} ${score * 3.6}deg, var(--border) 0deg)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: score > 80 ? 'var(--ok)' : 'var(--warn)' }}>{score}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Security Score</div>
              <div style={{ fontSize: 13, color: 'var(--muted-2)', marginTop: 2 }}>{passCount} checks passed · {warnCount} warnings</div>
              <div style={{ marginTop: 6 }}>
                <span className={`badge ${score > 80 ? 'badge-green' : 'badge-amber'}`}>
                  {score > 80 ? 'Good' : 'Needs Attention'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">Compliance</div></div>
          <div className="card-body">
            {[
              { name: 'STIR/SHAKEN', status: 'Compliant', badge: 'badge-green' },
              { name: 'E911 (Kari\'s Law)', status: 'Configured', badge: 'badge-green' },
              { name: 'GDPR / CCPA (call data retention)', status: '90-day policy active', badge: 'badge-green' },
              { name: 'SOC 2 Type II', status: 'Report available', badge: 'badge-blue' },
            ].map(c => (
              <div key={c.name} className="trunk-row">
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{c.name}</span>
                <span className={`badge ${c.badge}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Security checks */}
      <div className="card mb-20">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={16} color="var(--info)"/>
            <div className="card-title">Security Checklist</div>
          </div>
        </div>
        <div className="card-body">
          {CHECKS.map((c, i) => (
            <div key={i} className="trunk-row">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {c.status === 'pass'
                  ? <CheckCircle size={15} color="var(--ok)" style={{ flexShrink: 0, marginTop: 1 }}/>
                  : <AlertTriangle size={15} color="var(--warn)" style={{ flexShrink: 0, marginTop: 1 }}/>
                }
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.label}</div>
                  {c.detail && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 2 }}>{c.detail}</div>}
                </div>
              </div>
              <span className={`badge ${c.status === 'pass' ? 'badge-green' : 'badge-amber'}`}>
                {c.status === 'pass' ? 'Pass' : 'Warn'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color="var(--info)"/>
            <div className="card-title">Audit Log</div>
          </div>
          <button className="btn btn-secondary btn-sm">Export CSV</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {AUDIT_LOG.map((l, i) => (
                <tr key={i}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted-3)' }}>{l.ts}</span></td>
                  <td><span style={{ fontSize: 12, color: l.user === 'system' ? 'var(--purple)' : 'var(--info)' }}>{l.user}</span></td>
                  <td style={{ fontSize: 12 }}>{l.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
